import React, { useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import './ConfirmationModal.css';

/**
 * Reusable Confirmation Modal Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal message/description
 * @param {string} props.confirmText - Text for confirm button (default: "Confirm")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {Function} props.onConfirm - Callback when confirmed
 * @param {Function} props.onCancel - Callback when cancelled
 * @param {string} props.variant - Visual variant: 'danger', 'warning', 'info', 'default' (default: 'default')
 * @param {boolean} props.showCancelButton - Show cancel button (default: true)
 * @param {boolean} props.closeOnOverlayClick - Close modal when clicking overlay (default: true)
 */
const ConfirmationModal = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
  showCancelButton = true,
  closeOnOverlayClick = true
}) => {
  // Handle ESC key press
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape' && onCancel) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel]);

  // Manage body overflow
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Get icon based on variant
  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return <AlertCircle className="confirmation-modal-icon danger" size={24} />;
      case 'warning':
        return <AlertTriangle className="confirmation-modal-icon warning" size={24} />;
      case 'info':
        return <Info className="confirmation-modal-icon info" size={24} />;
      default:
        return null;
    }
  };

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget && onCancel) {
      onCancel();
    }
  };

  const modalContent = (
    <div className="confirmation-modal-overlay" onClick={handleOverlayClick}>
      <div className="confirmation-modal-container">
        <div className="confirmation-modal-header">
          <div className="confirmation-modal-title-wrapper">
            {getIcon()}
            <h2 className="confirmation-modal-title">{title}</h2>
          </div>
          {onCancel && (
            <button
              className="confirmation-modal-close"
              onClick={onCancel}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className="confirmation-modal-body">
          <div className="confirmation-modal-message">{message}</div>
        </div>

        <div className="confirmation-modal-footer">
          {showCancelButton && onCancel && (
            <button
              className="confirmation-modal-button cancel"
              onClick={onCancel}
            >
              {cancelText}
            </button>
          )}
          <button
            className={`confirmation-modal-button confirm ${variant}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.node.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['danger', 'warning', 'info', 'default']),
  showCancelButton: PropTypes.bool,
  closeOnOverlayClick: PropTypes.bool
};

ConfirmationModal.defaultProps = {
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'default',
  showCancelButton: true,
  closeOnOverlayClick: true
};

const arePropsEqual = (prev, next) =>
  prev.isOpen === next.isOpen &&
  prev.title === next.title &&
  prev.confirmText === next.confirmText &&
  prev.cancelText === next.cancelText &&
  prev.variant === next.variant &&
  prev.showCancelButton === next.showCancelButton &&
  prev.closeOnOverlayClick === next.closeOnOverlayClick;

ConfirmationModal.displayName = 'ConfirmationModal';

export default memo(ConfirmationModal, arePropsEqual);
